import { useCallback } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';
import { FIT_VIEW_DURATION, FIT_VIEW_PADDING } from './mindmap-constants';
import {
  MINDMAP_ZOOM_SLIDER_DURATION_MS,
  MINDMAP_ZOOM_STEP_DURATION_MS,
} from './mindmap-navigation';
import type { MindmapFlowEdge, MindmapFlowNode } from './mindmap-types';

interface UseMindmapZoomControlsInput {
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
  setZoomLevel: (zoomLevel: number) => void;
}

interface UseMindmapZoomControlsResult {
  handleFocusTree: () => void;
  handleZoomChange: (targetZoom: number) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
}

export function useMindmapZoomControls(
  input: UseMindmapZoomControlsInput,
): UseMindmapZoomControlsResult {
  const { reactFlowInstance, setZoomLevel } = input;

  const handleFocusTree = useCallback((): void => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.fitView({
      duration: FIT_VIEW_DURATION,
      padding: FIT_VIEW_PADDING,
    });
  }, [reactFlowInstance]);

  const handleZoomIn = useCallback((): void => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.zoomIn({ duration: MINDMAP_ZOOM_STEP_DURATION_MS });
  }, [reactFlowInstance]);

  const handleZoomOut = useCallback((): void => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.zoomOut({ duration: MINDMAP_ZOOM_STEP_DURATION_MS });
  }, [reactFlowInstance]);

  const handleZoomChange = useCallback((targetZoom: number): void => {
    if (!reactFlowInstance) {
      return;
    }

    setZoomLevel(targetZoom);
    reactFlowInstance.zoomTo(targetZoom, { duration: MINDMAP_ZOOM_SLIDER_DURATION_MS });
  }, [reactFlowInstance, setZoomLevel]);

  return {
    handleFocusTree,
    handleZoomChange,
    handleZoomIn,
    handleZoomOut,
  };
}
